/**
 * /admin/tests — Admin Test list page.
 *
 * Binding contract: `docs/ia/admin-tests.md`.
 *
 * Archetype: header + filter band + single sortable table panel. The simplest
 * workspace-list shape — the SME's editorial intent is "table of contents in
 * a technical manual: restrained, scannable, mono-numeric." A Test is a
 * heavyweight object — small in count, high in care-per-row.
 *
 * 15-FG/36 restores the 7-column table after `/admin/tests` shipped as an
 * empty `<UL>` (audit/29-ia-baseline.md § 3 verdict: deviates).
 *
 * Minimum columns (contract line 21):
 *   Title · Questions · Time · Pass · Attempts · Status · Actions
 *
 * Substrate (15-FG/30):
 *   - <AdminLayout> wraps <Shell mode='admin' densityMode='workspace'>.
 *   - Primitives only from `@/components/ui` (Heading / Eyebrow / Button /
 *     Card / Badge / Link).
 *   - design.md tokens only — no bg-bg-card / bg-surface / rounded-2xl /
 *     chromatic borders / second-accent fills.
 *
 * v1 data wiring: the live list-fetch (`listTests`) returns mock-shaped
 * envelopes in the dev DB; the contract permits mock data while the page
 * shape is being restored. The Attempts column reads `question_count` until
 * the dedicated attempt-aggregation endpoint ships — flagged in the row
 * render comment below.
 *
 * Cross-cutting invariants:
 *   - Inv 12 (no console.log) — none introduced.
 *   - Inv 13 (no `any` / @ts-ignore) — none.
 *   - Inv 14 (internal nav via <Link>) — all CTAs use <Link>.
 *   - Inv 15 (atoms from barrel) — Heading / Eyebrow / Button / Card / Badge /
 *     Link only.
 */

import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import {
  listTests,
  type Level,
  type TestPayload,
  type TestStatus,
  type Track,
} from '../../../state/tests/api.ts';
import { AdminLayout } from '../layout.tsx';
import { Badge, Button, Card, Heading, Link } from '@/components/ui';
import type { BadgeVariant } from '@/components/ui';

// ─── Constants ────────────────────────────────────────────────────────────────

const ALL_TRACKS: Array<Track | ''> = [
  '',
  'architecture',
  'mep',
  'structural',
  'interior',
];
const ALL_LEVELS: Array<Level | ''> = [
  '',
  'novice',
  'intermediate',
  'advanced',
  'bim_champion',
];
const ALL_STATUSES: Array<TestStatus | ''> = ['', 'draft', 'active', 'archived'];

/** Em-dash — the loading-state placeholder. Never "Loading…". */
const EM_DASH = '—';

/** Binding column spec from `docs/ia/admin-tests.md` line 21. */
interface ColumnSpec {
  id: 'title' | 'questions' | 'time' | 'pass' | 'attempts' | 'status' | 'actions';
  label: string;
  /** Columns 1–6 are sortable; Actions is not. */
  sortable: boolean;
  /** Numeric columns right-align + Space Mono per Polish 6. */
  numeric?: boolean;
}

const COLUMNS: readonly ColumnSpec[] = [
  { id: 'title', label: 'Title', sortable: true },
  { id: 'questions', label: 'Questions', sortable: true, numeric: true },
  { id: 'time', label: 'Time', sortable: true, numeric: true },
  { id: 'pass', label: 'Pass', sortable: true, numeric: true },
  { id: 'attempts', label: 'Attempts', sortable: true, numeric: true },
  { id: 'status', label: 'Status', sortable: true },
  { id: 'actions', label: 'Actions', sortable: false },
] as const;

type SortDir = 'asc' | 'desc';
interface SortState {
  column: ColumnSpec['id'];
  direction: SortDir;
}

// ─── Formatting helpers ───────────────────────────────────────────────────────

function formatInt(n: number): string {
  return n.toLocaleString('en-US');
}

function formatMinutes(min: number): string {
  return `${formatInt(min)} min`;
}

function formatPercent(n: number): string {
  return `${Math.round(n)}%`;
}

function statusVariant(status: TestStatus): BadgeVariant {
  switch (status) {
    case 'active':
      return 'success';
    case 'draft':
      return 'neutral';
    case 'archived':
      return 'warning';
    default:
      return 'neutral';
  }
}

/** Best-effort client-side sort. Server-side sort would replace this once
 *  the API supports an `order_by` param; for now we sort the loaded page. */
function sortItems(items: TestPayload[], sort: SortState | null): TestPayload[] {
  if (!sort) return items;
  const dir = sort.direction === 'asc' ? 1 : -1;
  const sorted = [...items];
  sorted.sort((a, b) => {
    switch (sort.column) {
      case 'title':
        return a.title.localeCompare(b.title) * dir;
      case 'questions':
        return (a.question_count - b.question_count) * dir;
      case 'time':
        return (a.time_limit - b.time_limit) * dir;
      case 'pass':
        return (a.pass_mark - b.pass_mark) * dir;
      case 'attempts':
        return (a.attempt_count - b.attempt_count) * dir;
      case 'status':
        return a.status.localeCompare(b.status) * dir;
      default:
        return 0;
    }
  });
  return sorted;
}

// ─── Composition helpers — co-located ─────────────────────────────────────────

interface SortIndicatorProps {
  active: boolean;
  direction: SortDir;
}

/** Chevron rendered to the right of a sortable column label. `muted` when
 *  inactive, `ink` when active. Sized 12px per Polish 1. */
function SortIndicator({ active, direction }: SortIndicatorProps): ReactElement {
  // Triangle glyph — no emoji per design.md restraint rule.
  const glyph = active && direction === 'asc' ? '▲' : '▼';
  return (
    <span
      aria-hidden="true"
      className={`ml-1.5 inline-block text-xs leading-none ${
        active ? 'text-ink' : 'text-muted'
      }`}
    >
      {glyph}
    </span>
  );
}

interface ColumnHeaderProps {
  spec: ColumnSpec;
  sort: SortState | null;
  onToggle: (id: ColumnSpec['id']) => void;
}

function ColumnHeader({ spec, sort, onToggle }: ColumnHeaderProps): ReactElement {
  const active = sort?.column === spec.id;
  const ariaSort: 'none' | 'ascending' | 'descending' = active
    ? sort.direction === 'asc'
      ? 'ascending'
      : 'descending'
    : 'none';
  const align = spec.numeric ? 'text-right' : 'text-left';
  const justify = spec.numeric ? 'justify-end' : 'justify-start';

  // Sortable header is a <button> inside the <th> per Polish 3 (sortable
  // headers are buttons; non-sortable headers are not focusable).
  return spec.sortable ? (
    <th
      scope="col"
      aria-sort={ariaSort}
      className={`${align} border-b border-line px-4 py-3`}
    >
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => onToggle(spec.id)}
        className={`!px-0 !py-0 !font-sans !text-eyebrow !font-medium uppercase tracking-wide text-muted hover:text-accent ${justify} w-full`}
      >
        {spec.label}
        <SortIndicator active={active} direction={sort?.direction ?? 'asc'} />
      </Button>
    </th>
  ) : (
    <th
      scope="col"
      className={`${align} border-b border-line px-4 py-3 font-sans text-eyebrow font-medium uppercase tracking-wide text-muted`}
    >
      {spec.label}
    </th>
  );
}

interface LoadingRowProps {
  columnCount: number;
}

function LoadingRow({ columnCount }: LoadingRowProps): ReactElement {
  return (
    <tr>
      {Array.from({ length: columnCount }, (_, j) => (
        <td
          key={j}
          className="px-4 py-3 font-mono text-mono text-muted"
        >
          {EM_DASH}
        </td>
      ))}
    </tr>
  );
}

interface EmptyStateRowProps {
  columnCount: number;
  children: ReactNode;
}

function EmptyStateRow({ columnCount, children }: EmptyStateRowProps): ReactElement {
  return (
    <tr>
      <td colSpan={columnCount} className="px-6 py-12 text-center">
        {children}
      </td>
    </tr>
  );
}

interface TestRowProps {
  test: TestPayload;
}

function TestRow({ test }: TestRowProps): ReactElement {
  return (
    <tr className="border-b border-line last:border-b-0 hover:bg-bg-warm">
      <td className="px-4 py-3 font-sans text-body text-ink">
        <Link
          to={`/admin/tests/${test.id}`}
          className="font-sans text-ink hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-l"
        >
          {test.title}
        </Link>
      </td>
      <td className="px-4 py-3 text-right font-mono text-mono text-ink-2">
        {formatInt(test.question_count)}
      </td>
      <td className="px-4 py-3 text-right font-mono text-mono text-ink-2">
        {formatMinutes(test.time_limit)}
      </td>
      <td className="px-4 py-3 text-right font-mono text-mono text-ink-2">
        {formatPercent(test.pass_mark)}
      </td>
      <td className="px-4 py-3 text-right font-mono text-mono text-ink-2">
        {formatInt(test.attempt_count)}
      </td>
      <td className="px-4 py-3">
        <Badge variant={statusVariant(test.status)}>{test.status}</Badge>
      </td>
      <td className="px-4 py-3 text-right">
        <Link
          to={`/admin/tests/${test.id}`}
          aria-label={`Edit ${test.title}`}
          className="font-sans text-small text-ink hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-l"
        >
          Edit
        </Link>
      </td>
    </tr>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function AdminTestListPage(): ReactElement {
  const [items, setItems] = useState<TestPayload[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [track, setTrack] = useState<Track | ''>('');
  const [level, setLevel] = useState<Level | ''>('');
  const [status, setStatus] = useState<TestStatus | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState | null>(null);

  const reload = (resetCursor: boolean): void => {
    void (async () => {
      setError(null);
      if (resetCursor) {
        setItems(null);
      }
      const env = await listTests({
        track: track === '' ? undefined : track,
        level: level === '' ? undefined : level,
        status: status === '' ? undefined : status,
        cursor: resetCursor ? undefined : (cursor ?? undefined),
      });
      if (!env.ok) {
        setError(`${env.error.code}: ${env.error.message}`);
        setItems([]);
        return;
      }
      setItems((prev) =>
        resetCursor || prev === null ? env.data.items : [...prev, ...env.data.items],
      );
      setCursor(env.data.next_cursor);
    })();
  };

  useEffect(() => {
    reload(true);
    // Mount-only effect — reload reads current filter state via closure.
    // Filter-driven reloads are kicked off explicitly by onChange handlers.
  }, []); // eslint-disable-line -- mount-only effect

  const onToggleSort = (id: ColumnSpec['id']): void => {
    setSort((prev) => {
      if (!prev || prev.column !== id) return { column: id, direction: 'asc' };
      return { column: id, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
    });
  };

  const isLoading = items === null;
  const sorted = items ? sortItems(items, sort) : [];
  const isEmpty = items !== null && items.length === 0;
  const headingId = 'admin-tests-region-heading';

  return (
    <AdminLayout title="Tests" eyebrow="Admin · Tests">
      {/* Header CTA cluster — right-aligned, sits beside the title block.
          AdminLayout already renders the title + eyebrow; we anchor the
          "+ New Test" CTA into the space below, top-right. The contract's
          editorial sub-label ("Create and manage your tests") sits as a
          quiet line above the CTA so screen readers find it before the
          action. 24px top margin establishes the header→panel rhythm. */}
      <div className="mt-6 flex flex-wrap items-start justify-between gap-4">
        <p className="font-sans text-small text-muted">
          Create and manage your tests
        </p>
        <Link
          to="/admin/tests/new"
          className="inline-flex items-center justify-center rounded-md bg-ink px-5 py-2.5 font-sans text-small font-medium text-bg no-underline hover:bg-ink-2 hover:text-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-l focus-visible:ring-offset-2"
        >
          + New Test
        </Link>
      </div>

      {error ? (
        <p role="alert" className="mt-4 font-sans text-small text-error">
          {error}
        </p>
      ) : null}

      {/* Table panel — single full-bleed Card containing the filter band +
          7-column table. Filter band attaches to the top of the Card per
          contract Extension rule ("border-radius: var(--radius) var(--radius)
          0 0 so the card visually integrates the filter"). */}
      <section
        role="region"
        aria-labelledby={headingId}
        className="mt-6"
      >
        {/* Accessible-name source for the region landmark. Visually hidden
            because the page-level h1 "Tests" already names the surface; the
            region label distinguishes the table panel from any future
            stat-strip region. */}
        <h2 id={headingId} className="sr-only">
          Tests table
        </h2>

        <Card variant="default" className="p-0">
          {/* Filter band — attached to the top of the Card. Spec-permitted
              extension. Narrow inline form, no card-internal padding mismatch
              with the table below. */}
          <div className="flex flex-wrap items-end gap-3 border-b border-line p-4">
            <label className="flex flex-col gap-1 font-sans text-small text-muted">
              <span className="font-mono text-eyebrow uppercase tracking-wide">
                Track
              </span>
              <select
                aria-label="Track"
                value={track}
                onChange={(e) => {
                  setTrack(e.target.value as Track | '');
                }}
                className="rounded-md border border-line-2 bg-card px-3 py-2 font-sans text-small text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l"
              >
                {ALL_TRACKS.map((t) => (
                  <option key={t} value={t}>
                    {t === '' ? 'All tracks' : t}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 font-sans text-small text-muted">
              <span className="font-mono text-eyebrow uppercase tracking-wide">
                Level
              </span>
              <select
                aria-label="Level"
                value={level}
                onChange={(e) => {
                  setLevel(e.target.value as Level | '');
                }}
                className="rounded-md border border-line-2 bg-card px-3 py-2 font-sans text-small text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l"
              >
                {ALL_LEVELS.map((l) => (
                  <option key={l} value={l}>
                    {l === '' ? 'All levels' : l}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 font-sans text-small text-muted">
              <span className="font-mono text-eyebrow uppercase tracking-wide">
                Status
              </span>
              <select
                aria-label="Status"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value as TestStatus | '');
                }}
                className="rounded-md border border-line-2 bg-card px-3 py-2 font-sans text-small text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l"
              >
                {ALL_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s === '' ? 'All statuses' : s}
                  </option>
                ))}
              </select>
            </label>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                reload(true);
              }}
            >
              Apply
            </Button>
          </div>

          {/* Table — 7 columns per binding contract. */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-small">
              <thead>
                <tr>
                  {COLUMNS.map((spec) => (
                    <ColumnHeader
                      key={spec.id}
                      spec={spec}
                      sort={sort}
                      onToggle={onToggleSort}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <>
                    <LoadingRow columnCount={COLUMNS.length} />
                    <LoadingRow columnCount={COLUMNS.length} />
                    <LoadingRow columnCount={COLUMNS.length} />
                  </>
                ) : isEmpty ? (
                  <EmptyStateRow columnCount={COLUMNS.length}>
                    <Heading level="h2" className="text-h3 text-ink">
                      No tests yet
                    </Heading>
                    <p className="mt-2 font-sans text-small text-muted">
                      Create your first test to get started.
                    </p>
                    <div className="mt-4 flex justify-center">
                      <Link
                        to="/admin/tests/new"
                        className="inline-flex items-center justify-center rounded-md bg-ink px-5 py-2.5 font-sans text-small font-medium text-bg no-underline hover:bg-ink-2 hover:text-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-l focus-visible:ring-offset-2"
                      >
                        + New Test
                      </Link>
                    </div>
                  </EmptyStateRow>
                ) : (
                  sorted.map((t) => <TestRow key={t.id} test={t} />)
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {cursor ? (
          <div className="mt-4 flex justify-center">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                reload(false);
              }}
            >
              Load more
            </Button>
          </div>
        ) : null}
      </section>
    </AdminLayout>
  );
}
